import { z } from "zod";
import type { RuleSetMeta } from "../types.js";

/**
 * lost-super — lost & ATO-held super finder + multiple-account fee-drag.
 *
 * Two surfaces, one module:
 *   1. `lostSuperLikelihood` — a TRIAGE from coarse job/move/name-change history
 *      that estimates the likelihood the user has lost or ATO-held super, anchored
 *      to the current ATO prize (population stats), and routes ONLY to the free
 *      myGov / ATO search. No dollar is computed for the user (it's unknowable);
 *      the output is a hedged likelihood band + the population context.
 *   2. `multipleAccountFeeDrag` — quantifies the recurring DUPLICATE admin-fee +
 *      insurance-premium leak of holding 2+ accounts, compounded to retirement. A
 *      transparent compounding illustration with disclosed assumptions — framed as
 *      a duplicate-cost illustration, never "you'll lose $X".
 *
 * HARD RULE (binding, panel C2 / contra-regulatory §C2): superannuation is an
 * s764A financial product with NO carve-out. No surface that displays this tool's
 * personalised output may name, rank, link, or imply a DESTINATION super fund to
 * consolidate into — that is the INFO 269 "dealing by arranging" / general-advice
 * kill-line. Every CTA ends at the ATO / myGov free process. Affiliate revenue is
 * $0 by design. The guard (`assertNoFundHandoff`) encodes this as code, not prose.
 */

// ---------------------------------------------------------------------------
// Shared metadata
// ---------------------------------------------------------------------------

/**
 * The current ATO population prize the triage is anchored to. Versioned per ATO
 * reporting cycle (these reprice annually). Never mutate a shipped year — author
 * a new ruleset file each ATO cycle.
 */
export interface LostSuperPrize {
  /** Total lost (fund-held) + ATO-held super, AUD. */
  totalDollars: number;
  /** Lost/unclaimed super accounts (count). */
  accounts: number;
  /** ATO's stated average amount per person/account, AUD. */
  averageParcel: number;
  /** Super reunited/paid out by the ATO since 2022, AUD. */
  reunitedSince2022Dollars: number;
  /** Accounts reunited/paid out by the ATO since 2022 (count). */
  reunitedSince2022Accounts: number;
  /** Australians holding 2+ super accounts (count) — the multiple-account base. */
  multipleAccountHolders: number;
  /** "As at" date the prize is reported for, ISO. */
  asAt: string;
}

/**
 * A likelihood band the triage can land in. Bands are ordered low → high; the
 * triage scores coarse risk signals and maps the score onto a band. The band is a
 * hedged TYPICAL-CASE likelihood, never a finding about the specific user.
 */
export interface LikelihoodBand {
  /** Stable key for the band. */
  key: "low" | "moderate" | "elevated" | "high";
  /** Human label, e.g. "Higher than average". */
  label: string;
  /** Inclusive lower bound of the risk score that lands in this band. */
  minScore: number;
  /** Exclusive upper bound; `null` = unbounded (top band). */
  maxScore: number | null;
}

export interface LostSuperRuleSet extends RuleSetMeta {
  /** ATO population prize the triage anchors to (the hero stat). */
  prize: LostSuperPrize;
  /** Ordered likelihood bands, authored to cover [0, ∞). */
  bands: readonly LikelihoodBand[];
  /**
   * Default assumptions for the fee-drag illustration. Every value is disclosed
   * in the output so the illustration is reproducible and clearly an
   * illustration, not a prediction.
   */
  feeDragDefaults: FeeDragAssumptions;
}

// ---------------------------------------------------------------------------
// Surface 1 — lostSuperLikelihood (triage)
// ---------------------------------------------------------------------------

/**
 * Coarse, non-identifying triage inputs. NO TFN, NO account numbers, NO fund
 * names — only bands the privacy posture allows. These feed a small decision tree
 * that scores the likelihood of detached / ATO-held super.
 */
export const LikelihoodInputSchema = z.object({
  /** How many employers the user has had (more jobs → more default accounts). */
  employerCount: z.number().int().min(0),
  /** Times the user has moved home since starting work (lost mail → lost super). */
  addressChanges: z.number().int().min(0).default(0),
  /** Whether the user has changed their legal name (e.g. marriage). */
  nameChanged: z.boolean().default(false),
  /** Whether the user knows they have ever let a super account go inactive/dormant. */
  hasDormantAccount: z.boolean().default(false),
  /** Whether the user has had any period of overseas / non-resident work. */
  workedOverseas: z.boolean().default(false),
  /** Whether the user has EVER consolidated / checked their super on myGov. */
  hasConsolidatedBefore: z.boolean().default(false),
  /** The reporting year being assessed, e.g. "2024-25". Passed in, never clocked. */
  assessmentYear: z.string(),
});

export type LikelihoodInput = z.infer<typeof LikelihoodInputSchema>;

export interface LostSuperLikelihoodOutput {
  /** The computed coarse risk score (sum of weighted signals). */
  score: number;
  /** The likelihood band key the score landed in. */
  bandKey: LikelihoodBand["key"];
  /** Human label for the band (hedged, typical-case). */
  bandLabel: string;
  /**
   * Population context the triage is anchored to — the hero stat. Always
   * surfaced so the "you probably have some" framing is grounded in the ATO
   * aggregate, not in a finding about this user.
   */
  prize: LostSuperPrize;
  /**
   * The free next action. ALWAYS the ATO / myGov process — never a fund. This is
   * a fixed routing target, not a recommendation between products.
   */
  freeAction: {
    /** What to do, in ATO's neutral phrasing. */
    label: string;
    /** Where it happens — the government surface. */
    destination: "myGov / ATO online services";
  };
}

// ---------------------------------------------------------------------------
// Surface 2 — multipleAccountFeeDrag (duplicate-cost illustration)
// ---------------------------------------------------------------------------

/**
 * The disclosed assumptions behind the compounding illustration. Every one is
 * echoed back in the output so the figure is reproducible and self-evidently an
 * illustration, not a prediction. Defaults live in the ruleset.
 */
export interface FeeDragAssumptions {
  /** Assumed annual real net investment return (fraction, e.g. 0.05 = 5%). */
  annualReturn: number;
  /** Assumed annual admin fee per duplicate account, AUD. */
  adminFeePerAccount: number;
  /** Assumed annual default insurance premium per duplicate account, AUD. */
  insurancePremiumPerAccount: number;
}

export const FeeDragInputSchema = z.object({
  /**
   * Number of super accounts the user holds. Must be ≥ 1; duplicates are
   * `accountCount − 1`. The illustration only sizes the cost of the DUPLICATES,
   * never of the (necessary) primary account.
   */
  accountCount: z.number().int().min(1),
  /** Years until the user expects to retire (the compounding horizon). */
  yearsToRetirement: z.number().int().min(0),
  /**
   * Whether the duplicate accounts carry default insurance the user is paying
   * for twice. When false, only the admin fee leaks (no insurance term).
   */
  duplicateInsurance: z.boolean().default(true),
  /** Optional override of the disclosed assumptions; missing fields use defaults. */
  assumptions: z
    .object({
      annualReturn: z.number().min(0).max(1).optional(),
      adminFeePerAccount: z.number().min(0).optional(),
      insurancePremiumPerAccount: z.number().min(0).optional(),
    })
    .optional(),
  /** The reporting year being assessed, e.g. "2024-25". Passed in, never clocked. */
  assessmentYear: z.string(),
});

export type FeeDragInput = z.infer<typeof FeeDragInputSchema>;

export interface MultipleAccountFeeDragOutput {
  /** Duplicate accounts (accountCount − 1). 0 when the user holds one account. */
  duplicateAccounts: number;
  /** Annual duplicate leak (admin + optional insurance) across all duplicates, AUD. */
  annualDuplicateCost: number;
  /** Years the illustration compounds over. */
  yearsToRetirement: number;
  /**
   * The simple SUM of the annual leak over the horizon (no compounding) — the
   * cash out-of-pocket. Whole dollars.
   */
  totalFeesPaid: number;
  /**
   * The leak COMPOUNDED at the assumed return to retirement — the illustrative
   * "could have grown to" figure (future value of an annuity). Whole dollars.
   */
  compoundedToRetirement: number;
  /** The assumptions actually used, echoed for transparency. */
  assumptionsUsed: FeeDragAssumptions & { duplicateInsurance: boolean };
}
